// The city lights at 500 m: NASA's Black Marble 2016 (Suomi NPP VIIRS
// Day/Night Band composite, public domain), the lights separated from the
// moonlit land under them, as a bundled tile pyramid built by
// scripts/build-night-imagery.mjs from NASA GIBS (layer VIIRS_Black_Marble).
// It streams through the body-generic imagery streamer (rocketry/
// planetimagery.js, rocketry/nightimagery.js) over the 8K earth_night.jpg.
//
// Grid: equirectangular CRS84, 512 px tiles, rootSpan 288 as in
// planetimagery.js and GIBS's own 500 m matrix set, levels 3..7 (36 down to
// 2.25 degree tiles; level 7 is 488 m per pixel at the equator). Paths are
// {z}/{y}/{x}.webp with y counted from the north and x from -180 lon. Only
// tiles with lights in them are built; NIGHT_IMAGERY_TILES (written by the
// build) says which, so the app never asks for one that is not there: the
// streamer draws those tiles from one shared slot of the offset, dark
// (emptyTexel, planetimagery.js). The 8K is not dark between towns (it
// carries the Black Marble's moonlit land, a navy that reads as light), so
// close in the lights take only what is over the floor below from it.
//
// Tiles hold display-encoded light (the Black Marble's own encoding, the
// same as earth_night.jpg), stored with an offset so darkness is never 0
// (the streamer reads 0 as no imagery). Decode the sampled channel s (0..1)
// as light = max(0, (s * 255 - OFFSET) / (255 - OFFSET)), then sRGB to
// linear. Lossy WebP leaves R, G and B within a level of each other; read one.
import { NIGHT_IMAGERY_BUILD, NIGHT_IMAGERY_TILES, NIGHT_IMAGERY_GAIN } from './nightImageryBuild.js';

export const NIGHT_IMAGERY_DATASET = 'blackmarble-2016';
export const NIGHT_IMAGERY_DATE = '2016-01-01';
export const NIGHT_IMAGERY_CACHE = `night-${NIGHT_IMAGERY_DATASET}-${NIGHT_IMAGERY_BUILD}`;
export const NIGHT_IMAGERY_PATH = `/textures/night-tiles/${NIGHT_IMAGERY_DATASET}`;
export const NIGHT_IMAGERY_OFFSET = 8;
// The data's floor, in its display values: lights from nothing at the first
// to all of them at the second (nightimagery.js, the cloud volume's glow).
// Below the first is the faint scattered light the sensor leaves a few
// kilometres round every town (0.06 to 0.07 on the lagoon beside
// Titusville), the lossy encoding's noise (0.004 to 0.02) and, in the 8K,
// the moonlit land (about 0.04 in these units over desert and ice, 0.015
// over the sea); Mims, a village in the woods, is 0.34 and Scottsmoor 0.24.
export const NIGHT_FLOOR = [0.08, 0.2];
// The page table is at the finest level, so there is no detail table: the
// globe's shader is at the 16 texture units WebGL allows.
export const NIGHT_IMAGERY_GRID = { tileSize: 512, rootSpan: 288, rootLevel: 3, pageLevel: 7, maxLevel: 7 };
export const nightImageryUrl = (z, x, y) => `${NIGHT_IMAGERY_PATH}/${z}/${y}/${x}.webp`;
export const NIGHT_IMAGERY_SOURCE_URL = (z, x, y) =>
  `https://gibs.earthdata.nasa.gov/wmts/epsg4326/best/VIIRS_Black_Marble/default/${NIGHT_IMAGERY_DATE}/500m/${z}/${y}/${x}.png`;
export { NIGHT_IMAGERY_GAIN };

// Whether the build wrote tile z/x/y (a bit per tile, row-major from the
// north-west, base64).
const bits = {};
export function nightTileAvailable(z, x, y) {
  const entry = NIGHT_IMAGERY_TILES[z];
  if (!entry) return false;
  if (!bits[z]) {
    const raw = typeof atob === 'function' ? atob(entry.bits) : Buffer.from(entry.bits, 'base64').toString('binary');
    bits[z] = Uint8Array.from(raw, (c) => c.charCodeAt(0));
  }
  const i = y * entry.width + x;
  return (bits[z][i >> 3] >> (i & 7) & 1) === 1;
}

export const NIGHT_IMAGERY_CREDIT = 'NASA Earth Observatory, Black Marble 2016 (Suomi NPP VIIRS), via NASA GIBS';
export const NIGHT_IMAGERY_CREDIT_URL = 'https://science.nasa.gov/earth/earth-observatory/earth-at-night/maps/';
